/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : RefreshButton
 *   Feature      : 226-universal-page-affordances
 *   Description  : Canonical page Refresh control — soft data re-fetch with
 *                  visual feedback. Spins while refreshing, disables mid-fetch,
 *                  pairs with the dual FreshnessIndicator, and on failure shows a
 *                  non-blocking error tint + tooltip WITHOUT clearing page
 *                  content (Spec 226 FR-011). Never reloads the page.
 *   Constitution : v1.23.0
 *   Author       : Peter Dobler
 *   Created      : 2026-07-07
 * ============================================================================
 */
import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from './base/Button';
import { AppTooltip } from './AppTooltip';
import { FreshnessIndicator } from './FreshnessIndicator';

export interface RefreshButtonProps {
  /** Soft re-fetch action (react-query refetch / invalidate). Must NOT reload the page. */
  onRefresh: () => void | Promise<void>;
  /** True while the underlying fetch is in flight. */
  isRefreshing?: boolean;
  /** Last client fetch time — shown in the freshness label. */
  lastFetchedAt?: Date | number | string | null;
  /** DB collection time (best-effort) — shown when available. */
  collectedAt?: Date | number | string | null;
  /** Last refresh error — drives the non-blocking error badge (content stays visible). */
  error?: unknown;
  /** Button label (default "Refresh"). */
  label?: string;
  className?: string;
}

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  onRefresh,
  isRefreshing = false,
  lastFetchedAt,
  collectedAt,
  error,
  label = 'Refresh',
  className,
}) => {
  const hasError = error != null;
  const errorMsg =
    error instanceof Error ? error.message : hasError ? 'Refresh failed — showing last data' : '';

  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      <FreshnessIndicator lastFetchedAt={lastFetchedAt} collectedAt={collectedAt} />
      {hasError && (
        <AppTooltip title={errorMsg || 'Refresh failed — showing last data'}>
          <span
            className="inline-flex items-center text-[var(--color-status-danger)]"
            aria-label="Refresh error"
          >
            <AlertTriangle className="h-4 w-4" />
          </span>
        </AppTooltip>
      )}
      <Button
        variant="outline"
        size="sm"
        // A failed refresh shows through `error` (the badge) with the last data kept;
        // the rejected promise itself has no other reader, so it ends here.
        onClick={() => { void Promise.resolve(onRefresh()).catch(() => undefined); }}
        disabled={isRefreshing}
        aria-label="Refresh page data"
        aria-busy={isRefreshing}
        className="shrink-0"
      >
        <RefreshCw className={`h-4 w-4 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
        <span className="ml-1.5">{label}</span>
      </Button>
    </div>
  );
};

export default RefreshButton;
